import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import moment from 'moment'
import {
    dispatchUrlPreview,
    IOrdinalProvenanceActor,
    IOrdinalProvenanceHop,
    IOrdinalProvenanceTenure,
} from 'api/api'
import UserIcon from 'components/users/UserIcon'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import OrdinalMarketControls from 'components/answers/FileEmbed/OrdinalMarketControls'
import useOrdinalDescriptor from 'hooks/useOrdinalDescriptor'
import useOpenRight from 'refactor/hooks/useOpenRight'
import { useInView } from 'react-intersection-observer'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import {
    extractOrdinalOutpointFromUrl,
    isCanonicalOrdinalContentUrl,
} from 'components/shared/ordinalUrlUtils'
import {
    decideOrdinalPreviewDispatch,
    selectOrdinalPreviewImage,
    shouldDispatchUrlPreviewAfterPreviewReady,
    type OrdinalPreviewReadyOptions,
} from 'components/shared/ordinalPreviewDispatch'
import {
    shouldRenderOrdinalWrapperChrome,
    shouldShowOrdinalOwnership,
} from 'components/panels/ordinalUpvalueFeature'
import OrdinalDetails from 'components/modals/OrdinalDetails'
import viewerStyles from 'components/modals/ordinals-viewer.module.scss'
import ArtifactMenu from './ArtifactMenu'
import { useOrdinalProvenance } from './ordinalProvenance'
import {
    artifactActorDisplayName,
    artifactActorIdentity,
    buildArtifactProvenancePresentation,
} from './artifactProvenancePresentation'
import type { IOrdinal, IOrdinalDescriptor } from 'api/api'
import type { CombinedOrdinal } from 'types/ordinals'
import styles from './artifact-badge.module.scss'

// Provenance loading lives in ./ordinalProvenance so the header and its menu
// share one per-origin cache.

interface IArtifactBadgeAnswer {
    id?: string
    message_type?: string | null
    is_system?: boolean | null
    content?: string | null
    url?: {
        address?: string | null
        preview_image_url?: string | null
        preview_url?: string | null
    } | null
}

export function isArtifactWrapperAnswer(answer: IArtifactBadgeAnswer | null | undefined): boolean {
    return shouldRenderOrdinalWrapperChrome(answer?.message_type, answer?.is_system, [
        answer?.url?.address,
        answer?.content,
    ])
}

// Artifact header on canonical-thread wrapper cards. Owns the whole title
// presentation above the standard controls row: flush hero artwork with an
// ordinals-stream crumb overlay (mirrors the thread stream breadcrumb), then
// owner ↔ creator, then the quiet metadata caption. The wrapper's system
// byline and generic link-preview card are suppressed in PostAnswer/Answer;
// the same wrapper+feature predicate gates all of it.
export default function ArtifactBadge({ answer }: { answer: IArtifactBadgeAnswer }) {
    if (!isArtifactWrapperAnswer(answer)) return null

    return <ArtifactHeader answer={answer} />
}

function ArtifactHeader({ answer }: { answer: IArtifactBadgeAnswer }) {
    const contentAddress = answer?.url?.address || answer?.content || null
    const origin = useMemo(() => {
        for (const candidate of [answer?.url?.address, answer?.content]) {
            const outpoint = candidate ? extractOrdinalOutpointFromUrl(String(candidate)) : null
            if (outpoint) return outpoint
        }
        return null
    }, [answer?.url?.address, answer?.content])

    const { openRight } = useOpenRight()
    const { ref: inViewRef, inView } = useInView({ triggerOnce: false })
    const [detailsOpen, setDetailsOpen] = useState(false)

    const { data: ordinalDescriptor } = useOrdinalDescriptor(origin, {
        assumeOrigin: !!origin && !!contentAddress && isCanonicalOrdinalContentUrl(contentAddress),
        enabled: !!origin,
    })
    // Latch the last resolved descriptor so transient refetches (EventBus
    // invalidations briefly returning pending) can't collapse the hero back to
    // the resolving placeholder and re-fire the content fetch.
    const lastResolvedRef = useRef<IOrdinalDescriptor | null>(null)
    const resolvedDescriptor =
        ordinalDescriptor?.status === 'resolved' && ordinalDescriptor.content_url
            ? ordinalDescriptor
            : null
    if (resolvedDescriptor) lastResolvedRef.current = resolvedDescriptor
    const stickyDescriptor =
        lastResolvedRef.current?.outpoint === origin ? lastResolvedRef.current : null
    const descriptor = resolvedDescriptor || stickyDescriptor

    const previewUrl = selectOrdinalPreviewImage({
        descriptorPreviewUrl: descriptor?.preview_url,
        urlPreviewUrl: answer?.url?.preview_image_url || answer?.url?.preview_url,
        contentType: descriptor?.content_type,
    })
    const previewDispatchDecision = decideOrdinalPreviewDispatch({
        answerId: answer?.id,
        descriptor: descriptor || ordinalDescriptor,
        previewUrl,
    })
    const detailsOrdinal = useMemo<CombinedOrdinal | null>(() => {
        if (!origin) return null
        const [txid, vout = '0'] = origin.split('_')
        const resolvedDetailsDescriptor = descriptor || ordinalDescriptor || undefined
        const ordinalId = resolvedDetailsDescriptor?.bsv_ordinal_id || null

        return {
            txid: txid || '',
            vout: Number(vout || 0),
            outpoint: origin,
            utxo_outpoint: origin,
            origin: {
                outpoint: origin,
                data: {
                    insc: {
                        file: {
                            type:
                                resolvedDetailsDescriptor?.content_type ||
                                'application/octet-stream',
                        },
                    },
                    map: { name: origin },
                },
            },
            treechatData: ordinalId
                ? ({ id: ordinalId, preview_url: previewUrl } as IOrdinal)
                : undefined,
            descriptor: resolvedDetailsDescriptor,
            content_type: resolvedDetailsDescriptor?.content_type || undefined,
            status: 'confirmed',
        }
    }, [descriptor, ordinalDescriptor, origin, previewUrl])
    const previewReadyDispatchRef = useRef<Set<string>>(new Set())
    const handlePreviewReady = useCallback(
        async (opts?: OrdinalPreviewReadyOptions) => {
            if (!contentAddress) return
            const dispatchKey = `${answer?.id || ''}:${contentAddress}:${opts?.previewUrl || ''}`
            const dispatched = previewReadyDispatchRef.current.has(dispatchKey)
            if (
                !shouldDispatchUrlPreviewAfterPreviewReady({
                    answerId: answer?.id,
                    previewUrl: opts?.previewUrl,
                    source: opts?.source,
                    previewAlreadyAvailable: !!previewUrl,
                    dispatched,
                })
            ) {
                return
            }

            previewReadyDispatchRef.current.add(dispatchKey)
            try {
                await dispatchUrlPreview({
                    answerId: answer?.id,
                    address: contentAddress,
                    ensureUrl: true,
                })
            } catch (err) {
                console.warn('Failed to ensure Url association after preview upload:', err)
            }
        },
        [answer?.id, contentAddress, previewUrl],
    )

    const openOrdinalsStream = useCallback(
        (ev: React.MouseEvent) => {
            ev.preventDefault()
            ev.stopPropagation()
            openRight({ ordinals: true, order: 'time' })
        },
        [openRight],
    )

    if (!origin) return null

    const isImageArtifact = String(descriptor?.content_type || '')
        .toLowerCase()
        .startsWith('image/')

    return (
        <>
            <div className={styles.artifactHeader} ref={inViewRef}>
                <div
                    data-artifact-media-frame
                    className={cn(
                        styles.hero,
                        !descriptor && styles.heroResolving,
                        descriptor && !isImageArtifact && styles.heroFrame,
                    )}
                >
                    <button
                        type="button"
                        className={styles.streamCrumb}
                        onClick={openOrdinalsStream}
                        title="Ordinals stream"
                        aria-label="Open the ordinals stream"
                        data-artifact-stream-crumb="true"
                    >
                        <OrdinalsIcon size={16} />
                        <span className={styles.streamCrumbLabel}>Ordinal</span>
                    </button>

                    <ArtifactMenu
                        answer={answer}
                        origin={origin}
                        onDetails={() => setDetailsOpen(true)}
                    />

                    {/* Anchored to the hero (not the renderer): the hero flex-centers
                        natural-sized media, so a renderer-anchored rail drifts below
                        the hero-anchored menu button by half the letterbox slack. */}
                    {descriptor?.bsv_ordinal_id && (
                        <OrdinalMarketControls
                            ordinalId={descriptor.bsv_ordinal_id}
                            origin={descriptor.origin_outpoint || origin || undefined}
                        />
                    )}

                    {descriptor?.origin_outpoint && descriptor?.content_url ? (
                        <OrdinalsRender
                            outpoint={descriptor.origin_outpoint}
                            url={descriptor.content_url}
                            bsvOrdinalId={descriptor.bsv_ordinal_id || undefined}
                            contentType={descriptor.content_type || undefined}
                            filename={descriptor.filename || undefined}
                            allowInteraction={true}
                            previewUrl={previewUrl}
                            autoCapture3dPreview={
                                inView &&
                                String(descriptor.content_type || '')
                                    .toLowerCase()
                                    .includes('model/gltf') &&
                                !previewUrl
                            }
                            answerId={answer?.id}
                            ensureUrl={!answer?.url?.preview_image_url && !!answer?.id}
                            onPreviewReady={handlePreviewReady}
                            fromLinkPreview={true}
                            naturalMedia={isImageArtifact}
                            fillVideoContainer={!isImageArtifact}
                            previewDispatchDecision={previewDispatchDecision}
                        />
                    ) : (
                        <span className={styles.resolvingLabel}>Resolving artifact…</span>
                    )}
                </div>

                <ArtifactOwnershipRow origin={origin} />
                <ArtifactMetaRow origin={origin} contentType={descriptor?.content_type} />
            </div>

            {detailsOpen &&
                detailsOrdinal &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={viewerStyles.lightbox}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Ordinal details"
                        onClick={event => {
                            if (event.target === event.currentTarget) setDetailsOpen(false)
                        }}
                    >
                        <OrdinalDetails
                            selectedOrdinal={{
                                ordinal: detailsOrdinal,
                                content: null,
                                isLoading: false,
                                error: null,
                                forceLive: false,
                            }}
                            inscriptionOutpoint={origin}
                            onClose={() => setDetailsOpen(false)}
                            onSendClick={() => undefined}
                            showShowInProfile={false}
                            showHidden={false}
                            showSend={false}
                        />
                    </div>,
                    document.body,
                )}
        </>
    )
}

// Row 1 under the artwork: two person cells in the style of an NFT
// provenance line — current owner (provenance popover) on the left, the
// creator / first-seen holder on the right. Each cell is a proper mini
// byline: avatar beside a stacked small-caps role label + bold name. When
// one person holds both roles the cells collapse into one with a role tag.
function ArtifactOwnershipRow({ origin }: { origin: string }) {
    const provenance = useOrdinalProvenance(origin)
    const presentation = useMemo(
        () => buildArtifactProvenancePresentation(provenance),
        [provenance],
    )
    const [ownersOrigin, setOwnersOrigin] = useState<string | null>(null)
    const ownersRef = useRef<HTMLDivElement | null>(null)
    const showOwners = ownersOrigin === origin

    useEffect(() => {
        if (!showOwners) return
        const onPointerDown = (ev: MouseEvent | TouchEvent) => {
            if (ownersRef.current && !ownersRef.current.contains(ev.target as Node)) {
                setOwnersOrigin(null)
            }
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('touchstart', onPointerDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('touchstart', onPointerDown)
        }
    }, [showOwners])

    const lastHop = presentation.hops[presentation.hops.length - 1]
    const lastHopActor = lastHop
        ? {
              address: lastHop.address,
              locking_script_hash: lastHop.locking_script_hash,
              user: lastHop.user,
              identity_basis: lastHop.identity_basis,
          }
        : null
    const primaryActor = presentation.current || lastHopActor
    const primaryRole = presentation.current
        ? ['pending', 'error'].includes(presentation.chainStatus || '')
            ? 'known owner'
            : 'owned by'
        : presentation.chainState === 'terminated'
        ? 'last held by'
        : presentation.chainState === 'indeterminate'
        ? 'last known holder'
        : 'provenance'
    const ownerCount = presentation.ownerCount
    const showOwnership = shouldShowOrdinalOwnership(!!primaryActor, ownerCount)
    const sameActor =
        artifactActorIdentity(primaryActor) !== null &&
        artifactActorIdentity(primaryActor) === artifactActorIdentity(presentation.secondary)
    const ownerTag = sameActor ? presentation.secondaryRole : null
    const secondaryIsUnresolvedSigner =
        presentation.secondaryRole === 'signed by' && !presentation.secondary?.user

    if (!showOwnership && !presentation.secondary) return null

    return (
        <div className={styles.ownershipRow}>
            {showOwnership && (
                <div className={styles.ownership} ref={ownersRef}>
                    <button
                        type="button"
                        className={cn(styles.personCell, styles.personCellButton)}
                        onClick={ev => {
                            ev.stopPropagation()
                            setOwnersOrigin(openOrigin => (openOrigin === origin ? null : origin))
                        }}
                        aria-expanded={showOwners}
                        title="Provenance"
                        data-testid="artifact-provenance-trigger"
                    >
                        {primaryActor?.user ? (
                            <UserIcon
                                user={primaryActor.user}
                                size="small"
                                contextStyles={cn(styles.personAvatar, styles.ownerAvatarRing)}
                            />
                        ) : (
                            <TreechatIcon
                                size={28}
                                className={cn(styles.personAvatar, styles.ownerAvatarRing)}
                            />
                        )}
                        <span className={styles.personText}>
                            <span className={styles.personLabel}>{primaryRole}</span>
                            <span
                                className={styles.personName}
                                data-testid="artifact-provenance-owner"
                            >
                                <span className={styles.personNameText}>
                                    {artifactActorDisplayName(primaryActor)}
                                </span>
                                {ownerTag && <span className={styles.personTag}>{ownerTag}</span>}
                                <i
                                    className={cn(
                                        'fa',
                                        showOwners ? 'fa-caret-up' : 'fa-caret-down',
                                        styles.personCaret,
                                    )}
                                />
                            </span>
                        </span>
                    </button>
                    {showOwners && provenance && (
                        <div
                            className={styles.popover}
                            onClick={ev => ev.stopPropagation()}
                            data-testid="artifact-provenance-popover"
                        >
                            <div className={styles.popoverTitle}>
                                Provenance{ownerCount > 1 ? ` · ${ownerCount} owners` : ''}
                                {presentation.stale ? ' · updating' : ''}
                                {presentation.chainState === 'terminated' ? ' · terminated' : ''}
                                {presentation.chainState === 'indeterminate'
                                    ? ' · current holder unconfirmed'
                                    : ''}
                            </div>
                            {presentation.hops.length > 0
                                ? presentation.hops.map(hop => (
                                      <ChainHopRow key={hop.outpoint} hop={hop} />
                                  ))
                                : presentation.legacyTenures.map((tenure, ix) => (
                                      <TenureRow
                                          key={`${tenure.user?.id || 'unknown'}-${
                                              tenure.acquired_at || ix
                                          }`}
                                          tenure={tenure}
                                          mintedByTreechat={!!provenance.minted_by_treechat}
                                      />
                                  ))}
                            {presentation.hops.length === 0 &&
                                presentation.legacyTenures.length === 0 && (
                                    <div className={styles.provenanceEmpty}>
                                        {presentation.chainStatus === 'error'
                                            ? 'Provenance unavailable'
                                            : 'Provenance is loading'}
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            )}

            {presentation.secondary && !sameActor && (
                <div
                    className={cn(styles.personCell, styles.creatorCell)}
                    data-testid="artifact-provenance-secondary"
                >
                    {presentation.secondary.user ? (
                        <UserIcon
                            user={presentation.secondary.user}
                            size="small"
                            contextStyles={styles.personAvatar}
                        />
                    ) : (
                        <span className={cn(styles.personAvatar, styles.personAvatarUnknown)}>
                            <i
                                className={cn(
                                    'fa',
                                    secondaryIsUnresolvedSigner ? 'fa-key' : 'fa-link',
                                )}
                            />
                        </span>
                    )}
                    <span className={styles.personText}>
                        <span className={styles.personLabel}>
                            {secondaryIsUnresolvedSigner
                                ? 'signed by wallet'
                                : presentation.secondaryRole}
                        </span>
                        <span className={styles.personName}>
                            <span className={styles.personNameText}>
                                {artifactActorDisplayName(presentation.secondary)}
                            </span>
                        </span>
                    </span>
                </div>
            )}
        </div>
    )
}

// Row 2: quiet plumbing caption — origin outpoint (tap to copy), content type,
// minted/first-seen date, chain link. Secondary colors throughout.
export function ArtifactMetaRow({
    origin,
    contentType,
}: {
    origin: string | null
    contentType?: string | null
}) {
    const provenance = useOrdinalProvenance(origin)
    const presentation = useMemo(
        () => buildArtifactProvenancePresentation(provenance),
        [provenance],
    )
    const [copiedOrigin, setCopiedOrigin] = useState<string | null>(null)
    const copyResetRef = useRef<number | null>(null)

    useEffect(
        () => () => {
            if (copyResetRef.current) window.clearTimeout(copyResetRef.current)
        },
        [],
    )

    if (!origin) return null

    const shortOrigin = `${origin.slice(0, 8)}…${origin.slice(-6)}`
    const explorerUrl = bsvTxExplorerUrl(origin.split('_')[0])
    const copied = copiedOrigin === origin

    const mintEvidence = presentation.mintEvidence
    const mintEvidenceText = mintEvidence
        ? mintEvidence.timestamp
            ? `${mintEvidence.label} ${moment(mintEvidence.timestamp).format('MMM YYYY')}`
            : mintEvidence.blockHeight
            ? `${mintEvidence.label} ${mintEvidence.blockHeight}`
            : mintEvidence.label
        : null

    const normalizedType = (contentType || '').split(';')[0].trim().toLowerCase()

    const copyOrigin = async (ev: React.MouseEvent) => {
        ev.stopPropagation()
        try {
            await navigator.clipboard.writeText(origin)
            setCopiedOrigin(origin)
            if (copyResetRef.current) window.clearTimeout(copyResetRef.current)
            copyResetRef.current = window.setTimeout(
                () => setCopiedOrigin(copiedValue => (copiedValue === origin ? null : copiedValue)),
                1500,
            )
        } catch {
            // Clipboard unavailable (permissions/insecure context) — leave the
            // full outpoint reachable via the chip's title attribute.
        }
    }

    return (
        <div className={styles.metaRow}>
            <button
                type="button"
                className={cn(styles.metaChip, styles.metaOrigin)}
                onClick={copyOrigin}
                title={`${origin} — copy origin outpoint`}
                aria-label="Copy origin outpoint"
            >
                <span className={styles.metaOriginText}>{shortOrigin}</span>
                <i className={cn('fa', copied ? 'fa-check' : 'fa-clone')} />
            </button>
            {normalizedType && <span className={styles.metaItem}>{normalizedType}</span>}
            {mintEvidenceText && (
                <span className={styles.metaItem} data-testid="artifact-mint-evidence">
                    {mintEvidenceText}
                </span>
            )}
            {explorerUrl && (
                <a
                    className={cn(styles.metaChip, styles.metaLink)}
                    href={explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View origin transaction"
                    onClick={ev => ev.stopPropagation()}
                >
                    on-chain <i className="fa fa-external-link" />
                </a>
            )}
        </div>
    )
}

function ActorAvatar({
    actor,
    heldByTreechat = false,
}: {
    actor: IOrdinalProvenanceActor | null
    heldByTreechat?: boolean
}) {
    return actor?.user ? (
        <UserIcon user={actor.user} size="small" contextStyles={styles.tenureAvatar} />
    ) : heldByTreechat ? (
        <TreechatIcon size={24} className={styles.tenureAvatar} />
    ) : (
        <span className={cn(styles.tenureAvatar, styles.tenureAvatarUnknown)}>
            <i className="fa fa-link" />
        </span>
    )
}

function ChainHopRow({ hop }: { hop: IOrdinalProvenanceHop }) {
    const actor: IOrdinalProvenanceActor = {
        address: hop.address,
        locking_script_hash: hop.locking_script_hash,
        user: hop.user,
        identity_basis: hop.identity_basis,
    }
    const acquired = hop.acquired_at ? moment(hop.acquired_at).format('MMM D, YYYY') : null
    const released = hop.released_at ? moment(hop.released_at).format('MMM D, YYYY') : null
    const range = hop.current
        ? acquired
            ? `since ${acquired}`
            : hop.block_height
            ? `block ${hop.block_height}`
            : 'current'
        : [acquired || (hop.block_height ? `block ${hop.block_height}` : null), released]
              .filter(Boolean)
              .join(' – ')
    const txId = hop.tx_id || hop.outpoint.split('_')[0]

    return (
        <div
            className={cn(styles.tenureRow, hop.current && styles.tenureRowCurrent)}
            data-testid="artifact-provenance-hop"
        >
            <ActorAvatar actor={actor} heldByTreechat={hop.current} />
            <a
                className={styles.tenureName}
                href={bsvTxExplorerUrl(txId)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={ev => ev.stopPropagation()}
            >
                {artifactActorDisplayName(actor)}
            </a>
            {hop.sequence === 0 && (
                <span className={cn(styles.tenureTag, styles.tenureTagGenesis)}>origin</span>
            )}
            {hop.current && <span className={styles.tenureTag}>current</span>}
            <span className={styles.tenureRange}>{range}</span>
        </div>
    )
}

function TenureRow({
    tenure,
    mintedByTreechat,
}: {
    tenure: IOrdinalProvenanceTenure
    mintedByTreechat: boolean
}) {
    const name = tenure.user
        ? tenure.user.name || tenure.user.username || tenure.user.handle
        : 'unknown'
    const acquired = tenure.acquired_at ? moment(tenure.acquired_at).format('MMM D, YYYY') : null
    const released = tenure.released_at ? moment(tenure.released_at).format('MMM D, YYYY') : null
    const range = tenure.current
        ? acquired
            ? `since ${acquired}`
            : 'current'
        : [acquired, released].filter(Boolean).join(' – ')

    return (
        <div className={cn(styles.tenureRow, tenure.current && styles.tenureRowCurrent)}>
            {tenure.user ? (
                <UserIcon user={tenure.user} size="small" contextStyles={styles.tenureAvatar} />
            ) : (
                <span className={cn(styles.tenureAvatar, styles.tenureAvatarUnknown)}>
                    <i className="fa fa-question" />
                </span>
            )}
            <span className={styles.tenureName}>{name}</span>
            {tenure.current && <span className={styles.tenureTag}>current</span>}
            {tenure.source === 'genesis' && (
                <span className={cn(styles.tenureTag, styles.tenureTagGenesis)}>
                    {mintedByTreechat ? 'minted' : 'first seen'}
                </span>
            )}
            <span className={styles.tenureRange}>{range}</span>
        </div>
    )
}
